import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import type { ShadowSettingsLegacy } from '@sites/entity-core';
import { createShadowSettingsLegacy } from '@sites/entity-core';

import type { ColorPickerEventPayload } from '../sp-color-picker/src/types/color-picker.type';
import { SpColorPickerComponent } from '../sp-color-picker/src/sp-color-picker.component';
import { SpInputNumComponent } from '../sp-form-controls/sp-input-num/sp-input-num.component';
import {
    pickedShadowColor,
    shadowColorAlpha,
    withShadowColorAlpha,
} from '../sp-shadow-settings-signal/src/shadow-color';

/**
 * Signal-form editor for the box-shadow settings — the signal-form analogue of
 * the FormGroup-based `sp-shadow-settings` panel. Mirrors that template
 * (offset X/Y, blur, spread, colour + alpha) but drives state through Angular
 * signals; the value shape is the canonical `ShadowSettingsLegacy` from
 * `@sites/entity-core`, i.e. exactly what a widget signal form's
 * `shadowSettings` branch holds.
 *
 * Contract (same as `sp-animation-settings-signal-legacy`):
 *
 *  - Parent binds a snapshot of its signal-form shadow value via the
 *    `shadowSettings` input; changes flow in and resync the local model.
 *  - Edits update the local model and emit the next `ShadowSettingsLegacy`
 *    via `valueChangeEvent`; the parent calls `form.setShadowSettings(value)`
 *    so the signal form (and history dispatch) stays the single source of truth.
 */
@Component({
    selector: 'sp-shadow-settings-signal-legacy',
    templateUrl: './sp-shadow-settings-signal-legacy.component.html',
    styleUrls: ['./sp-shadow-settings-signal-legacy.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, SpColorPickerComponent, SpInputNumComponent],
})
export class SpShadowSettingsSignalLegacyComponent {
    public readonly shadowSettings = input<ShadowSettingsLegacy | null>(null);
    public readonly valueChangeEvent = output<ShadowSettingsLegacy>();

    private readonly model = signal<ShadowSettingsLegacy>(createShadowSettingsLegacy());

    public readonly offsetXValue = computed(() => this.model().offsetX);
    public readonly offsetYValue = computed(() => this.model().offsetY);
    public readonly blurValue = computed(() => this.model().blur);
    public readonly spreadValue = computed(() => this.model().spread);
    public readonly colorValue = computed(() => this.model().color);
    public readonly colorAlphaValue = computed(() => shadowColorAlpha(this.model().color));

    constructor() {
        effect(() => {
            const incoming = this.shadowSettings();
            if (!incoming) return;
            this.model.set(incoming);
        });
    }

    // --- Setters (template event handlers) ---

    public setOffsetX(value: number): void {
        this.patch({ offsetX: value });
    }

    public setOffsetY(value: number): void {
        this.patch({ offsetY: value });
    }

    public setBlur(value: number): void {
        this.patch({ blur: value });
    }

    public setSpread(value: number): void {
        this.patch({ spread: value });
    }

    public setColorAlpha(alpha: number): void {
        this.patch({ color: withShadowColorAlpha(this.model().color, alpha) });
    }

    public setShadowColor({ value }: ColorPickerEventPayload): void {
        this.patch({ color: pickedShadowColor(value, this.colorAlphaValue()) });
    }

    private patch(patch: Partial<ShadowSettingsLegacy>): void {
        this.model.update((m) => ({ ...m, ...patch }));
        this.valueChangeEvent.emit(this.model());
    }
}
